import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { getCatalogBrochureForPrint } from "../store/service";
import "./ProductsList.css";
import "./CatalogBrochure.css";
import { getStoreLogoPath } from "../utils";
import { ReactComponent as WarrantyBadgeIcon } from "../assets/img/warranty_badge.svg";
import { ReactComponent as DeliveryBadgeIcon } from "../assets/img/delivery_badge.svg";
import catalogBrochureStub from "./stubs/catalogBrochureStub.json";

const USE_CATALOG_BROCHURE_STUB = false;

/** Matches product title / brochure accent */
const BROCHURE_ACCENT = "#d97708";

const WARRANTY_YEARS_BY_RANGE = {
  eco: 7,
  prime: 10,
  gold: 15,
};

const getWarrantyYears = (product) => {
  const range = String(product?.range ?? "")
    .trim()
    .toLowerCase();
  return WARRANTY_YEARS_BY_RANGE[range] ?? 3;
};

const getDimensionValues = (min, max, step) => {
  const minN = Number(min);
  const maxN = Number(max);
  const stepN = Number(step);
  if (
    Number.isNaN(minN) ||
    Number.isNaN(maxN) ||
    Number.isNaN(stepN) ||
    stepN <= 0
  ) {
    return [];
  }

  const values = [];
  for (let v = minN; v <= maxN; v += stepN) values.push(v);
  return values;
};

const PACKING_MAX_ROWS_FIRST_COL = 7;

const splitPackingRowsForTwoColumns = (rows) => {
  if (rows.length <= PACKING_MAX_ROWS_FIRST_COL) {
    return { left: rows, right: [], useTwoCols: false };
  }
  return {
    left: rows.slice(0, PACKING_MAX_ROWS_FIRST_COL),
    right: rows.slice(PACKING_MAX_ROWS_FIRST_COL),
    useTwoCols: true,
  };
};

/** Height vs width in mm — prefers store_product, else dimensions_config defaults. */
const getProductHeightWidthMm = (product) => {
  const sp = product?.store_product;
  const hSp = Number(sp?.height);
  const wSp = Number(sp?.width);
  if (Number.isFinite(hSp) && Number.isFinite(wSp) && hSp > 0 && wSp > 0) {
    return { height: hSp, width: wSp };
  }
  const cfg = product?.dimensions_config;
  if (!cfg || typeof cfg !== "object") return null;
  const height = Number(cfg.default_height ?? cfg.max_height ?? cfg.min_height);
  const width = Number(cfg.default_width ?? cfg.max_width ?? cfg.min_width);
  if (
    !Number.isFinite(height) ||
    !Number.isFinite(width) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { height, width };
};

/** Taller than wide (e.g. wardrobe) → side-by-side brochure images; else stacked. */
const isVerticalDominantProduct = (product) => {
  const dims = getProductHeightWidthMm(product);
  if (!dims) return false;
  return dims.height / dims.width > 1.5;
};

const PACKING_TABLE_CELL_PAD = { padding: "1mm" };

const renderPackingSubTable = (rows, tableKey) => (
  <table
    key={tableKey}
    className="table table-bordered mb-0 catalog-brochure-packing-table"
    style={{ flex: "1 1 0", minWidth: 0, width: "100%" }}
  >
    <thead>
      <tr>
        <th
          className="bg-body-secondary"
          scope="col"
          style={PACKING_TABLE_CELL_PAD}
        >
          Item
        </th>
        <th
          className="bg-body-secondary"
          scope="col"
          style={PACKING_TABLE_CELL_PAD}
        >
          Count
        </th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row, index) => (
        <tr key={`${tableKey}-${row.item}-${index}`}>
          <td style={PACKING_TABLE_CELL_PAD}>{row.item}</td>
          <td style={PACKING_TABLE_CELL_PAD}>{`${row.count} nos.`}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const renderDimensions = (product) => {
  const cfg = product?.dimensions_config;
  if (!cfg) return null;

  const dimensions = ["heights", "widths", "depths"];

  const hasAnyNonStandard = dimensions.some(
    (label) => (cfg[`non_standard_${label}`] ?? []).length > 0
  );

  return (
    <div
      style={{
        borderTopStyle: "dotted",
        borderTopColor: "#666",
        borderTopWidth: 1,
        marginTop: "3mm",
        paddingTop: "3mm",
      }}
    >
      {dimensions.map((label) => {
        const standard = cfg[`standard_${label}`];
        const nonStandard = cfg[`non_standard_${label}`] ?? [];
        const title = `${label[0].toUpperCase() + label.slice(1)}:`;

        const nonStandardSet = new Set(nonStandard);
        const sorted = standard
          .concat(nonStandard)
          .sort((a, b) => Number(a) - Number(b));

        return (
          <div key={label} className="d-flex align-items-baseline gap-1">
            <span className="fw-bold">{title}</span>
            <span>
              {sorted.map((dim, i) => (
                <span key={dim}>
                  {dim}
                  {nonStandardSet.has(dim) && (
                    <sup style={{ fontSize: "8pt" }}>*</sup>
                  )}
                  {i < sorted.length - 1 && ", "}
                </span>
              ))}
            </span>
          </div>
        );
      })}
      {hasAnyNonStandard && (
        <div className="mt-1" style={{ fontSize: "7pt" }}>
          <sup>*</sup> Against order — 3 day delivery
        </div>
      )}
    </div>
  );
};

const getPackingRows = (packingList, product) => {
  if (!packingList) return [];

  if (Array.isArray(packingList)) {
    const typeOrder = { panel: 0, hardware: 1, fixture: 2 };
    const productLabel =
      product?.base_code || product?.name || product?.id || "(unknown product)";
    const sorted = packingList
      .filter(
        (entry) =>
          entry &&
          typeof entry === "object" &&
          ["panel", "hardware", "fixture"].includes(entry.type)
      )
      .sort((a, b) => (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99));

    const rows = [];
    for (const entry of sorted) {
      const name = String(entry.name ?? "").trim();
      const qtyRaw = entry.qty;
      const qtyStr =
        qtyRaw === undefined || qtyRaw === null ? "" : String(qtyRaw).trim();
      if (!name) {
        console.error(
          "[CatalogBrochure] packing_list entry missing non-empty name (fix API payload)",
          { product: productLabel, entry }
        );
        continue;
      }
      if (!qtyStr) {
        console.error(
          "[CatalogBrochure] packing_list entry missing qty (fix API payload)",
          { product: productLabel, entry }
        );
        continue;
      }
      rows.push({ item: name, count: qtyStr });
    }
    return rows;
  }

  if (typeof packingList !== "object") return [];

  // Backward compatibility with older object-format payloads.
  return Object.entries(packingList).flatMap(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.entries(value).map(([nestedKey, nestedValue]) => ({
        item: nestedKey,
        count: String(nestedValue),
      }));
    }
    return [{ item: key, count: String(value) }];
  });
};

const renderProduct = (product) => {
  const images = Array.isArray(product.images) ? product.images : [];
  const mainImage = images[0];
  const secondaryImage = images[1];
  const warrantyYears = getWarrantyYears(product);
  const packingRows = getPackingRows(product.packing_list, product);
  const {
    left: packingLeft,
    right: packingRight,
    useTwoCols,
  } = splitPackingRowsForTwoColumns(packingRows);
  const useSideBySideImages = isVerticalDominantProduct(product);

  return (
    <div
      className="d-flex flex-column bg-white"
      style={{ height: "100%", minHeight: 0, padding: "3mm" }}
    >
      <div>
        <div className="d-flex justify-content-between align-items-baseline gap-2">
          <h1
            className="fw-bold mb-0"
            style={{
              fontSize: "10pt",
              lineHeight: "1.15",
              color: BROCHURE_ACCENT,
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            {product.name}
          </h1>
          <span
            className="text-muted fw-bold lh-1 text-end"
            style={{ fontSize: "8pt", flexShrink: 0 }}
          >
            {product.base_code}
          </span>
        </div>
        <hr className="my-2 opacity-100" />
      </div>
      <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
        {/* Row 1: 35% images | 65% description — vertical-dominant products: side-by-side; else stacked */}
        <div
          className="d-flex"
          style={{ gap: "4mm", alignItems: "flex-start" }}
        >
          <div
            className="d-flex flex-column"
            style={{
              flex: "35 1 0",
              minWidth: 0,
              alignSelf: "stretch",
            }}
          >
            <div
              className={useSideBySideImages ? "d-flex" : "d-flex flex-column"}
              style={{
                gap: "2mm",
                width: "100%",
                ...(useSideBySideImages ? { alignItems: "flex-start" } : {}),
              }}
            >
              {mainImage && (
                <img
                  src={mainImage}
                  alt={product.name}
                  style={{
                    width:
                      useSideBySideImages && secondaryImage ? "50%" : "100%",
                    height: "auto",
                    maxHeight: useSideBySideImages ? "25mm" : "15mm",
                    display: "block",
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              )}
              {secondaryImage && (
                <img
                  src={secondaryImage}
                  alt={`${product.name} secondary`}
                  style={{
                    width: useSideBySideImages ? "50%" : "100%",
                    height: "auto",
                    maxHeight: useSideBySideImages ? "25mm" : "15mm",
                    display: "block",
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              )}
            </div>
            <span
              className="mt-2 text-center lh-sm border-top border-1 pt-2"
              style={{
                fontSize: "7pt",
                borderTopStyle: "dotted",
                borderTopColor: "#666",
              }}
            >
              Only carcass. Shutters not included.
            </span>
          </div>

          <div
            className="d-flex flex-column"
            style={{
              flex: "65 1 0px",
              minWidth: "0px",
              borderLeft: "1px dotted #666",
              paddingLeft: "10px",
            }}
          >
            <div className="text-muted" style={{ lineHeight: "1.4" }}>
              <div>{product.description}</div>
              <div className="mt-2">
                Made of{" "}
                <span className="fw-bold">{product.board || "N/A"}</span> in{" "}
                <span className="fw-bold">{product.color || "N/A"}</span>.
              </div>
            </div>
            {renderDimensions(product)}
          </div>
        </div>
        {packingRows.length > 0 && (
          <div className="mt-3">
            <div>
              <div className="fw-bold mb-1">Packing List</div>
              <div
                className="d-flex"
                style={{ gap: "4mm", alignItems: "flex-start" }}
              >
                {renderPackingSubTable(packingLeft, "packing-a")}
                {useTwoCols ? (
                  renderPackingSubTable(packingRight, "packing-b")
                ) : (
                  <div style={{ flex: "1 1 0", minWidth: 0 }} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div
        className="mt-auto d-flex align-items-center pt-2"
        style={{
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          color: BROCHURE_ACCENT,
          fontWeight: 600,
          fontSize: "inherit",
          gap: "5mm",
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: "1.5mm" }}>
          <DeliveryBadgeIcon
            aria-hidden
            focusable="false"
            style={{
              width: "3.4mm",
              height: "3.4mm",
              display: "block",
              flexShrink: 0,
            }}
          />
          <span>1 day delivery</span>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "1.5mm" }}>
          <WarrantyBadgeIcon
            aria-hidden
            focusable="false"
            style={{
              width: "3.4mm",
              height: "3.4mm",
              display: "block",
              flexShrink: 0,
            }}
          />
          <span>{warrantyYears} year warranty</span>
        </div>
      </div>
    </div>
  );
};

const renderProductPage = (products, pageNumber) => (
  <div
    className="brochure-print-page d-flex flex-column"
    style={{ minHeight: "694px" }}
  >
    <div
      className="d-flex flex-column"
      style={{
        fontSize: "8pt",
        gap: "7mm",
        marginTop: "3mm",
        marginBottom: "3mm",
        flex: 1,
        minHeight: 0,
      }}
    >
      <div className="d-flex flex-column h-100" style={{ gap: "3mm" }}>
        {[0, 1].map((rowIdx) => (
          <div
            key={`row-${rowIdx}`}
            className="d-flex flex-row"
            style={{ gap: "3mm", flex: 1, minHeight: 0 }}
          >
            {[0, 1].map((colIdx) => {
              const index = rowIdx * 2 + colIdx;
              const product = products[index];

              return (
                <div
                  key={`slot-${index}`}
                  className={`d-flex flex-column bg-white h-100 ${
                    !product && "bg-transparent"
                  }`}
                  style={{ flex: 1, minHeight: 0 }}
                >
                  {product ? (
                    renderProduct(product)
                  ) : (
                    <div style={{ flex: 1 }} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
    <div
      className="d-flex justify-content-between fw-bold mt-auto"
      style={{ fontSize: "9pt" }}
    >
      <span className="text-muted text-uppercase">zerobuild.in</span>
      <span className="text-muted">{pageNumber}</span>
    </div>
  </div>
);

const renderBanner = (banner) => (
  <div className="brochure-print-banner-full brochure-print-banner">
    {banner?.image ? (
      <img
        src={banner.image}
        alt="Banner"
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    ) : null}
  </div>
);

const CatalogBrochure = () => {
  const [searchParams] = useSearchParams();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const queryParams = Object.fromEntries(searchParams.entries());
    fetchBrochure(queryParams);

    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        @page {
          size: 297mm 210m;
          margin: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [searchParams]);

  const fetchBrochure = async (queryParams = {}) => {
    try {
      if (USE_CATALOG_BROCHURE_STUB) {
        if (Array.isArray(catalogBrochureStub)) {
          setPages(catalogBrochureStub);
        } else if (Array.isArray(catalogBrochureStub?.pages)) {
          setPages(catalogBrochureStub.pages);
        } else {
          setPages([]);
        }
        return;
      }

      const response = await getCatalogBrochureForPrint(queryParams);
      if (Array.isArray(response)) {
        setPages(response);
      } else if (Array.isArray(response?.pages)) {
        setPages(response.pages);
      } else {
        setPages([]);
      }
    } catch (err) {
      setError(err?.error || err?.message || "Failed to fetch brochure");
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="catalog-products-list-container">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="brochure-print-print-root price-print-view d-flex flex-column">
        {(() => {
          const items = [];
          const productBuffer = [];

          const flushProductBuffer = (keyPrefix) => {
            if (productBuffer.length === 0) return;
            let chunkIndex = 0;
            for (let i = 0; i < productBuffer.length; i += 4) {
              items.push({
                type: "productGroup",
                key: `${keyPrefix}-chunk-${chunkIndex}`,
                products: productBuffer.slice(i, i + 4),
              });
              chunkIndex += 1;
            }
            productBuffer.length = 0;
          };

          pages.forEach((page, pageIndex) => {
            if (page.type === "banner") {
              flushProductBuffer(`pre-banner-${pageIndex}`);
              items.push({ type: "banner", key: `banner-${pageIndex}`, page });
              return;
            }
            const products = Array.isArray(page.products) ? page.products : [];
            products.forEach((product) => {
              productBuffer.push(product);
            });
          });
          flushProductBuffer("final-products");

          const productPageItems = items.filter(
            (i) => i.type === "productGroup"
          );
          const productPageIndexByKey = new Map(
            productPageItems.map((i, idx) => [i.key, idx + 1])
          );

          return items.map((item) => {
            if (item.type === "banner") {
              return (
                <React.Fragment key={item.key}>
                  {renderBanner(item.page)}
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={item.key}>
                {renderProductPage(
                  item.products,
                  productPageIndexByKey.get(item.key)
                )}
              </React.Fragment>
            );
          });
        })()}
      </div>
    </>
  );
};

export default CatalogBrochure;
